import {
  Button,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  Input,
  makeStyles,
  mergeClasses,
  OverlayDrawer,
  shorthands,
  Switch,
  Tab,
  TabList,
  tokens,
} from "@fluentui/react-components";
import {
  CursorClick20Regular,
  Dismiss20Regular,
  Lightbulb16Regular,
  Map20Regular,
  MyLocation16Regular,
  Play16Regular,
  Search16Regular,
} from "@fluentui/react-icons";
import {
  CSSProperties,
  memo,
  ReactNode,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from "react";
import { locateElement } from "./dom";
import { HelpAsk } from "./HelpAsk";
import type { HelpNotice } from "./HelpLauncher";
import { HelpTooltip } from "./HelpTooltip";
import type { HelpLanguage, HelpStep, PageHelp } from "./types";
import {
  groupStepsBySection,
  localizedContext,
  localizedPage,
  localizedSection,
  localizedStep,
  roleBadgeLabel,
  stepMatchesQuery,
} from "./types";

/**
 * The page guide: the readable manual for the current screen, including the parts behind a
 * dialog or another view. The tour shows only what is on screen; the drawer deliberately does
 * not, so a field inside the "Add employee" dialog is readable without opening it.
 */

const DRAWER_WIDTH = 440;
/** How much of the panel stays on screen while it stands aside, so it never reads as closed. */
const YIELD_SLIVER = 14;

const useStyles = makeStyles({
  drawer: {
    // `translate` rather than `transform` for the peek: Fluent's drawer motion animates
    // `transform` with the Web Animations API, which would win over an inline transform.
    // The two properties compose, so the peek never fights the open/close animation.
    transitionProperty: "translate",
    transitionDuration: "225ms",
    transitionTimingFunction: "ease",
  },
  header: {
    ...shorthands.borderBottom("1px", "solid", tokens.colorNeutralStroke2),
    paddingBottom: "16px",
  },
  tabs: {
    marginTop: "8px",
    marginLeft: "-10px",
  },
  headRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    columnGap: "8px",
  },
  eyebrow: {
    fontSize: "10px",
    fontWeight: tokens.fontWeightBold,
    letterSpacing: "1.2px",
    textTransform: "uppercase",
    color: tokens.colorNeutralForegroundDisabled,
  },
  title: {
    fontSize: "18px",
    fontWeight: tokens.fontWeightBold,
    lineHeight: 1.25,
    marginTop: "2px",
    color: tokens.colorNeutralForeground1,
  },
  intro: {
    fontSize: "12.5px",
    lineHeight: 1.6,
    color: tokens.colorNeutralForeground2,
    marginTop: "12px",
  },
  buttonRow: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: "8px",
    rowGap: "8px",
    marginTop: "14px",
  },
  hint: {
    fontSize: "11.5px",
    lineHeight: 1.5,
    color: tokens.colorNeutralForeground3,
    marginTop: "6px",
  },
  search: {
    width: "100%",
    marginTop: "12px",
  },
  body: {
    paddingBottom: "24px",
  },
  matchLine: {
    fontSize: "11.5px",
    color: tokens.colorNeutralForeground3,
    marginTop: "12px",
  },
  sectionRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    marginTop: "20px",
    marginBottom: "8px",
  },
  sectionLabel: {
    fontSize: "10px",
    fontWeight: tokens.fontWeightBold,
    letterSpacing: "1.2px",
    textTransform: "uppercase",
    color: tokens.colorNeutralForeground3,
    whiteSpace: "nowrap",
  },
  sectionRule: {
    flexGrow: 1,
    height: "1px",
    backgroundColor: tokens.colorNeutralStroke2,
  },
  card: {
    display: "flex",
    alignItems: "flex-start",
    columnGap: "8px",
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius("12px"),
    ...shorthands.padding("12px"),
    marginBottom: "8px",
    backgroundColor: tokens.colorNeutralBackground1,
  },
  cardText: {
    flexGrow: 1,
    minWidth: 0,
  },
  cardTitleRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    columnGap: "6px",
    rowGap: "4px",
  },
  cardTitle: {
    fontSize: "13.5px",
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  cardBody: {
    fontSize: "12.5px",
    lineHeight: 1.55,
    color: tokens.colorNeutralForeground2,
    marginTop: "4px",
  },
  cardHint: {
    fontSize: "11.5px",
    lineHeight: 1.5,
    fontStyle: "italic",
    color: tokens.colorNeutralForeground3,
    marginTop: "6px",
  },
  cardHintMissing: {
    color: tokens.colorStatusWarningForeground1,
  },
  cardActions: {
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
  },
  cardAction: {
    color: tokens.colorNeutralForeground3,
    ":hover": {
      color: tokens.colorBrandForeground1,
    },
  },
  badge: {
    fontSize: "10px",
    fontWeight: tokens.fontWeightBold,
    letterSpacing: "0.4px",
    textTransform: "uppercase",
    color: "#9a5a00",
    backgroundColor: "#fff1d6",
    ...shorthands.borderRadius(tokens.borderRadiusCircular),
    ...shorthands.padding("1px", "6px"),
    whiteSpace: "nowrap",
  },
  tips: {
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  tip: {
    display: "flex",
    alignItems: "flex-start",
    columnGap: "8px",
    fontSize: "12.5px",
    lineHeight: 1.55,
    color: tokens.colorNeutralForeground2,
  },
  tipIcon: {
    flexShrink: 0,
    marginTop: "2px",
    color: tokens.colorBrandForeground1,
  },
  footer: {
    display: "block",
    ...shorthands.borderTop("1px", "solid", tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground2,
    paddingTop: "10px",
    paddingBottom: "10px",
  },
  footerCaption: {
    fontSize: "11px",
    lineHeight: 1.5,
    color: tokens.colorNeutralForeground3,
  },
});

const SectionLabel = ({ children }: { children: ReactNode }) => {
  const styles = useStyles();
  return (
    <div className={styles.sectionRow}>
      <span className={styles.sectionLabel}>{children}</span>
      <span className={styles.sectionRule} />
    </div>
  );
};

const StepCardBody = ({
  step,
  language,
  contextHint,
  onLocate,
  onStartHere,
}: {
  step: HelpStep;
  language: HelpLanguage;
  /** How to reveal the control, when the step lives behind a UI action. */
  contextHint?: string;
  /** Flashes the control and reports whether it was on screen. */
  onLocate: (step: HelpStep) => boolean;
  /** Opens the tour ON this step, so a card found by searching is one click from the walk. */
  onStartHere: (step: HelpStep) => void;
}) => {
  const styles = useStyles();
  // Only set after a failed locate: the drawer does not probe the DOM up front, so a step is
  // never greyed out on a stale measurement taken before the user acted.
  const [missing, setMissing] = useState(false);
  const badge = roleBadgeLabel(step);
  const { title, body, translated } = localizedStep(step, language);

  return (
    <div className={styles.card}>
      <div className={styles.cardText}>
        <div className={styles.cardTitleRow}>
          <span className={styles.cardTitle}>{title}</span>
          {badge && <span className={styles.badge}>{badge}</span>}
          {!translated && <span className={styles.badge}>EN</span>}
        </div>
        <div className={styles.cardBody}>{body}</div>
        {(missing || step.context) && contextHint && (
          <div className={mergeClasses(styles.cardHint, missing && styles.cardHintMissing)}>
            {contextHint}
          </div>
        )}
        {missing && !contextHint && (
          <div className={mergeClasses(styles.cardHint, styles.cardHintMissing)}>
            Not on screen right now.
          </div>
        )}
      </div>
      <div className={styles.cardActions}>
        <HelpTooltip content="Show me where this is" positioning="before">
          <Button
            appearance="subtle"
            size="small"
            icon={<MyLocation16Regular />}
            aria-label={`Locate: ${title}`}
            className={styles.cardAction}
            onClick={() => setMissing(!onLocate(step))}
          />
        </HelpTooltip>
        <HelpTooltip content="Walk me through from here" positioning="before">
          <Button
            appearance="subtle"
            size="small"
            icon={<Play16Regular />}
            aria-label={`Start the tour at: ${title}`}
            className={styles.cardAction}
            onClick={() => onStartHere(step)}
          />
        </HelpTooltip>
      </div>
    </div>
  );
};

/**
 * Memoised because of the search box: typing re-rendered every card (each with two tooltips)
 * before the character was painted. Props are stable per card, so a keystroke now re-renders
 * only the cards the filter added or removed.
 */
const StepCard = memo(StepCardBody);
StepCard.displayName = "StepCard";

interface HelpDrawerProps {
  open: boolean;
  help: PageHelp;
  /** Role-filtered steps — the same list the launcher hands to the tour. */
  steps: HelpStep[];
  /** Whether the floating "?" is shown over the page (see ./preference). */
  floating: boolean;
  onFloatingChange: (on: boolean) => void;
  language: HelpLanguage;
  onClose: () => void;
  /** Starts the tour — from the beginning, or at one step when the reader picked one. */
  onStartTour: (from?: HelpStep) => void;
  /** Hands over to "Point at a field". */
  onInspect: () => void;
  /** Where the Ask tab's notices go (a miss after navigating) — the app's toast system. */
  notify: (notice: HelpNotice) => void;
}

export const HelpDrawer = ({
  open,
  help,
  steps,
  floating,
  onFloatingChange,
  language,
  onClose,
  onStartTour,
  onInspect,
  notify,
}: HelpDrawerProps) => {
  const styles = useStyles();
  /**
   * Two halves, as in MGA: "This page" answers "what is this control?", "Ask" answers "where do I
   * do X?" anywhere in the app. Component state in a drawer that stays mounted, so the tab survives
   * close and reopen within a session.
   */
  const [tab, setTab] = useState<"page" | "ask">("page");
  /**
   * Filters the entries on this page. Matching runs over the BODIES too, for the reader who can
   * describe the field but not name it. Deferred rather than debounced: there is no request to
   * spare, only rendering, so the list follows the typing instead of blocking it.
   */
  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);
  const matches = useMemo(
    () => steps.filter((step) => stepMatchesQuery(step, deferredQuery)),
    [steps, deferredQuery]
  );
  const groups = useMemo(
    () => groupStepsBySection(help.sections, matches),
    [help.sections, matches]
  );
  /**
   * Set while the panel stands aside so a control underneath it can be seen. Not a close: the
   * scroll position and the query survive, and it slides back once the flash has faded.
   */
  const [yielding, setYielding] = useState(false);
  // A guide reopened mid-peek must open in place, not pushed aside.
  useEffect(() => {
    if (!open) setYielding(false);
  }, [open]);
  const page = localizedPage(help, language);

  const locate = useCallback(
    (step: HelpStep): boolean => locateElement(step, { onYield: setYielding }),
    []
  );

  const isNarrow = typeof window !== "undefined" && window.innerWidth < 640;
  const width = isNarrow ? "100vw" : `${DRAWER_WIDTH}px`;

  return (
    <OverlayDrawer
      open={open}
      position="end"
      onOpenChange={(_event, data) => {
        if (!data.open) onClose();
      }}
      // Marks this as the guide's own panel, not the dialog a tour should stay inside.
      data-help-panel="drawer"
      aria-label={`Page guide: ${page.title}`}
      className={styles.drawer}
      style={
        {
          width,
          "--fui-Drawer--size": width,
          translate: yielding ? `calc(100% - ${YIELD_SLIVER}px) 0` : "0 0",
        } as CSSProperties
      }
      // Full contrast for the flash: the scrim dims the very control being pointed at.
      // `background-color`, not `opacity`, which Fluent's fade motion owns.
      backdrop={{
        style: {
          backgroundColor: yielding ? "transparent" : undefined,
          transition: "background-color 180ms ease",
        },
      }}
    >
      <DrawerHeader className={styles.header}>
        <div className={styles.headRow}>
          <div>
            <div className={styles.eyebrow}>Page guide</div>
            <div className={styles.title}>{page.title}</div>
          </div>
          <Button
            appearance="subtle"
            icon={<Dismiss20Regular />}
            aria-label="Close page guide"
            onClick={onClose}
          />
        </div>

        <TabList
          className={styles.tabs}
          size="small"
          selectedValue={tab}
          onTabSelect={(_event, data) => setTab(data.value as "page" | "ask")}
        >
          <Tab value="page" data-help="help-tab-page">
            This page
          </Tab>
          <Tab value="ask" data-help="help-tab-ask">
            Ask
          </Tab>
        </TabList>

        {tab === "page" && (
          <>
            <div className={styles.intro}>{page.intro}</div>

            <div className={styles.buttonRow}>
              <Button
                appearance="primary"
                size="small"
                icon={<Map20Regular />}
                onClick={() => onStartTour()}
                data-help="help-tour-button"
              >
                Show me around
              </Button>
              {/* The way out of a long walk: the reader who wants ONE field points at it instead
                  of stepping through everything in front of it. */}
              <Button
                appearance="outline"
                size="small"
                icon={<CursorClick20Regular />}
                onClick={onInspect}
                data-help="help-inspect-button"
              >
                Point at a field
              </Button>
            </div>
            <div className={styles.hint}>
              The tour visits what is on screen right now — or only the open dialog, when there is
              one; its step counter jumps to any of them. “Point at a field” answers the other way
              round: click the control you are looking at and read what it does.
            </div>

            <Input
              className={styles.search}
              size="small"
              value={query}
              onChange={(_event, data) => setQuery(data.value)}
              contentBefore={<Search16Regular />}
              maxLength={60}
              aria-label="Search this page’s guide"
              placeholder={`Search all ${steps.length} entries…`}
              data-help="help-page-search"
            />
          </>
        )}
      </DrawerHeader>

      <DrawerBody className={styles.body}>
        {tab === "ask" ? (
          <HelpAsk
            language={language}
            // Navigating away leaves the guide describing a screen the user has left.
            onNavigated={onClose}
            onYield={setYielding}
            notify={notify}
          />
        ) : (
          <>
            {deferredQuery.length > 0 && (
              <div className={styles.matchLine}>
                {matches.length === 0
                  ? "Nothing on this page matches. The “Ask” tab searches the whole app."
                  : `${matches.length} of ${steps.length} entries match “${deferredQuery}”.`}
              </div>
            )}

            {groups.map((group) => (
              <div key={group.section}>
                <SectionLabel>{localizedSection(help, group.section, language)}</SectionLabel>
                {group.steps.map((step) => (
                  <StepCard
                    key={step.selector}
                    step={step}
                    language={language}
                    contextHint={
                      step.context ? localizedContext(help, step.context, language) : undefined
                    }
                    onLocate={locate}
                    onStartHere={onStartTour}
                  />
                ))}
              </div>
            ))}

            {/* Hidden while filtering: the tips answer no search, and leaving them under an empty
                result reads as a match. */}
            {deferredQuery.length === 0 && page.tips && page.tips.length > 0 && (
              <>
                <SectionLabel>Good to know</SectionLabel>
                <div className={styles.tips}>
                  {page.tips.map((tip) => (
                    <div key={tip} className={styles.tip}>
                      <Lightbulb16Regular className={styles.tipIcon} />
                      <span>{tip}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </DrawerBody>

      {/* Where the floating "?" is turned back on — off by default because the bottom-right
          corner belongs to the tables' pagination and row actions. */}
      <DrawerFooter className={styles.footer}>
        <Switch
          checked={floating}
          onChange={(_event, data) => onFloatingChange(data.checked)}
          label="Floating “?” button"
        />
        <div className={styles.footerCaption}>
          {floating
            ? "Shown in the bottom-right corner, where it can cover a table’s pagination and row buttons."
            : "Off — the “?” in the top bar opens this guide, and stays clickable while a dialog is open."}
        </div>
      </DrawerFooter>
    </OverlayDrawer>
  );
};
